import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { RawAssemblyTree } from '@/state'

import { useMemo, useState } from 'react'
import { createSubAssemblyTree } from './lib/createSubAssemblyTree'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { useCADPageStore } from '@/state/cad'
import { TreeNodes } from './TreeNodes'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { createStepOrderNumber } from '../AssemblyOrderPanel/lib/createStepOrderNumber'
import { isEmptyOrSpaces } from '@/utils'

interface SubAssemblyTreeProps {
  tree?: RawAssemblyTree
  gltf: GLTFObject
}
export const SubAssemblyTree = ({ tree, gltf }: SubAssemblyTreeProps) => {
  const { steps } = useOperationSteps()
  const [isPartsInOperationExpanded, setIsPartsInOperationExpanded] =
    useState(true)
  const [isAllPartsExpanded, setIsAllPartsExpanded] = useState(true)

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId,
  )

  // This is the tree for "Parts in operation"
  const partsInOperationTree = useMemo(
    () => createSubAssemblyTree({ tree, steps, subAssemblyDocumentPageId }),
    [tree, steps, subAssemblyDocumentPageId],
  )

  // Get the parent step and its display name
  const parentStepDisplayName = useMemo(() => {
    if (!subAssemblyDocumentPageId) return 'operation'

    const parentStep = steps.find((s) => s.id === subAssemblyDocumentPageId)
    if (!parentStep) return 'operation'

    // If step has a name, use it
    if (parentStep.name && !isEmptyOrSpaces(parentStep.name)) {
      return parentStep.name
    }

    // Otherwise, use the order number (e.g., "1.1.1")
    return createStepOrderNumber({ step: parentStep, steps })
  }, [subAssemblyDocumentPageId, steps])

  // Create "All Parts" tree - everything NOT in the subTree
  const allPartsTree = useMemo(() => {
    if (!tree || !partsInOperationTree) {
      return tree
    }

    // Get all node UUIDs that are in the subTree (excluding the virtual root)
    const subTreeNodeIds = new Set(
      partsInOperationTree.nodes
        .filter((n) => n.uuid !== partsInOperationTree.root)
        .map((n) => n.uuid),
    )

    // Filter out nodes that are in the subTree
    const nodesNotInOperation = tree.nodes.filter(
      (node) => !subTreeNodeIds.has(node.uuid),
    )

    return nodesNotInOperation.length > 0
      ? {
          ...tree,
          nodes: nodesNotInOperation,
        }
      : null
  }, [tree, partsInOperationTree])

  if (!partsInOperationTree) {
    return null
  }

  return (
    <div className="pb-2 flex flex-col">
      {/* Parts in operation section */}
      {partsInOperationTree && (
        <div className="flex flex-col">
          <button
            className="flex items-center gap-2 px-4 py-2 text-xs italic text-gray-500 hover:bg-gray-50"
            onClick={() =>
              setIsPartsInOperationExpanded(!isPartsInOperationExpanded)
            }
          >
            {isPartsInOperationExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
            <span>Parts in operation {parentStepDisplayName}</span>
          </button>
          {isPartsInOperationExpanded && (
            <TreeNodes tree={partsInOperationTree} gltf={gltf} height="auto" />
          )}
        </div>
      )}

      {/* All Parts section */}
      {allPartsTree && (
        <div className="flex flex-col">
          <button
            className="flex items-center gap-2 px-4 py-2 text-xs italic text-gray-500 hover:bg-gray-50"
            onClick={() => setIsAllPartsExpanded(!isAllPartsExpanded)}
          >
            {isAllPartsExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" />
            )}
            <span>All Parts</span>
          </button>
          {isAllPartsExpanded && (
            <TreeNodes tree={allPartsTree} gltf={gltf} height="auto" />
          )}
        </div>
      )}
    </div>
  )
}
